import { Box, InputAdornment, useTheme } from "@mui/material";
import { useCallback, useRef, useState } from "react";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";
import StyledTextField from "@/modules/common/components/input/styled-text-field";
import useUsersFilterOptions from "@/modules/common/hooks/lookups/users-filter-options.hook";
import CustomAutoComplete from "@/modules/common/components/input/dropdowns/auto-complete";

type Props = {
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  salesAgentDefaultEmail: string | undefined;
  setSalesAgentDefaultEmail: (value: string | undefined) => void;
  inviterId?: string | undefined;
  setInviterId?: (value: string | undefined) => void;
};

const Filters: React.FC<Props> = ({
  searchTerm,
  setSearchTerm,
  salesAgentDefaultEmail,
  setSalesAgentDefaultEmail,
  inviterId,
  setInviterId,
}) => {
  const theme = useTheme();
  const { isMobile } = useScreenSize();
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const [localSearch, setLocalSearch] = useState(searchTerm);

  const {
    usersFilterOptions,
    usersFilterOptionsLoading,
    handleSearchChange: handleSearchUsers,
  } = useUsersFilterOptions();

  const handleDebouncedSearch = useCallback(
    (value: string, delay = 600) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        setSearchTerm(value.trim());
      }, delay);
    },
    [setSearchTerm]
  );

  const userOptions = (usersFilterOptions ?? []).map((user) => ({
    label: user.name,
    value: user.uid,
  }));

  const defaultUserOption = userOptions.find(
    (opt) => opt.value === (inviterId || salesAgentDefaultEmail)
  );

  return (
    <Box
      sx={{
        display: "flex",
        gap: theme.nawy.spacing.xs,
        transition: "opacity 0.2s ease",
        alignItems: isMobile ? "stretch" : "center",
        flexDirection: isMobile ? "column" : "row",
      }}
    >
      <Box sx={{ minWidth: isMobile ? "340px" : "320px" }}>
        <StyledTextField
          size="small"
          variant="outlined"
          placeholder="Search by name or number"
          value={localSearch}
          onChange={(e) => {
            setLocalSearch(e.target.value);
            handleDebouncedSearch(e.target.value);
          }}
          sx={{ width: "100%" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlinedIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box sx={{ display: "flex", minWidth: "180px" }}>
        <CustomAutoComplete
          fullWidth
          placeholder={isMobile ? "Select Invitee user" : "Invitee user"}
          options={userOptions}
          loading={usersFilterOptionsLoading}
          defaultValue={defaultUserOption || null}
          handleSearch={handleSearchUsers}
          onChange={(selected: any) => {
            const value = selected?.value;
            if (setInviterId) {
              setInviterId(value);
            } else {
              setSalesAgentDefaultEmail(value);
            }
          }}
          onClose={() => handleSearchUsers("")}
        />
      </Box>
    </Box>
  );
};

export default Filters;
